// ============================================================
// Dexie-Datenbankschema v1–4 + Migrations
// Spezifikation: Prompt v6.7 Abschnitt E
// Regel: Versionsnummern sind FINAL — niemals ändern oder löschen!
// ============================================================

import Dexie, { type Table } from 'dexie';
import type {
  MandantID, MitarbeiterID, ArbeitsplatzID,
  LeistungsID, RechnungsNummer, EuroCents, ConfidenceScore,
} from '../types/branded';

// ── Entity-Interfaces ──────────────────────────────────────────────────────

export interface CryptoMeta {
  id: string;
  value: Uint8Array;
}

export interface MandantKontakt {
  name:    string;
  titel:   string;
  email:   string;
  telefon: string;
}

export interface Mandant {
  id: MandantID;
  schema_version: 1;
  kurz_code: string;           // z.B. "RAM", "AMA", "PLA"
  name: string;
  farbe: string;               // Hex-Farbe für UI
  // Calendar-Matching-Felder (Modul 11.1)
  color_id?: string;           // Google Calendar color_id
  email_domains: string[];     // z.B. ["ramsauer.at"]
  location_keywords: string[]; // z.B. ["Schladming", "Planai"]
  title_aliases: string[];     // z.B. ["Ramsauer", "RAM"]
  aktiv: boolean;
  erstellt_am: string;         // ISO-8601
  // Kontakt- und Organisationsdaten (ab v6.9)
  strasse?: string;
  plz?: string;
  ort?: string;
  land?: string;
  telefon?: string;
  email?: string;
  hauptkontakt?: MandantKontakt;
  fuehrungspersonen?: MandantKontakt[];
  branche?: string;
  mitarbeiterzahl?: number;
  bemerkungen?: string;
}

export interface Mitarbeiter {
  id: MitarbeiterID;
  schema_version: 1;
  mandant_id: MandantID;
  arbeitsplatz_id: ArbeitsplatzID;
  // Klarnamen werden VERSCHLÜSSELT gespeichert (Uint8Array als Base64)
  name_enc: string;            // encrypted: Nachname, Vorname
  geburtsdatum_enc: string;    // encrypted: ISO-Datum
  geschlecht: 'M' | 'W' | 'D' | 'U';
  aktiv: boolean;
  erstellt_am: string;
  geaendert_am: string;
}

export interface Arbeitsplatz {
  id: ArbeitsplatzID;
  schema_version: 1;
  mandant_id: MandantID;
  bezeichnung: string;
  gefahren_codes: string[];    // VGÜ-relevante Gefahrencodes
  intervall_monate: number;    // Standard-Untersuchungsintervall
  aktiv: boolean;
}

export interface VorsorgeEintrag {
  id: string;
  schema_version: 1 | 2;
  mitarbeiter_id: MitarbeiterID;
  arbeitsplatz_id: ArbeitsplatzID;
  mandant_id: MandantID;
  vgue_tatbestand_id: string;
  faelligkeit_datum: string;   // ISO-Datum
  letztes_datum?: string;      // ISO-Datum der letzten Untersuchung
  status: 'OFFEN' | 'ERLEDIGT' | 'UEBERFAELLIG' | 'AUSGESCHIEDEN';
  notizen_enc?: string | undefined; // optional verschlüsselt
  erstellt_am: string;
  geaendert_am: string;
}

export interface AuditLog {
  id?: number;                 // autoincrement
  schema_version: 1;
  timestamp: string;           // ISO-8601
  firma: string;               // Mandant-Kurzcode oder 'SYSTEM'
  aktion: string;              // z.B. 'DB_OPENED', 'LOGIN', 'RECHNUNG_ERSTELLT'
  details?: string | undefined; // JSON-String, niemals Klarnamen!
  user_agent?: string | undefined;
}

// ── Version 2: Calendar-Sync ───────────────────────────────────────────────

export interface CalendarSyncEntry {
  id: string;                  // Google Event ID
  schema_version: 1;
  iCalUID: string;
  status: 'PENDING' | 'MATCHED' | 'IGNORED' | 'MANUAL';
  synced_at: string;
  matched_mandant_id?: MandantID;
  matched_leistung_id?: LeistungsID;
  confidence_score?: ConfidenceScore;
  raw_title_enc: string;       // verschlüsselt (Datenschutz!)
  start_datetime: string;
  end_datetime: string;
  dauer_minuten: number;
}

// ── Version 3: Honorar + Leistungserfassung ────────────────────────────────

export interface Honorarvereinbarung {
  id: string;
  schema_version: 1;
  mandant_id: MandantID;
  gueltig_ab: string;          // ISO-Datum
  gueltig_bis?: string;        // ISO-Datum; undefined = unbegrenzt
  monatspauschale?: EuroCents;
  stundensatz?: EuroCents;
  fahrtkosten_km?: EuroCents;  // Cent pro km
  rabatt_prozent?: number;     // 0–100
  notizen?: string;
}

export interface Leistungserfassung {
  id: string;
  schema_version: 1;
  datum: string;               // ISO-Datum
  mandant_id: MandantID;
  leistung_id: LeistungsID;
  dauer_minuten?: number;
  abgerechnet: boolean;
  rechnungs_id?: string;
  calendar_sync_id?: string;   // Referenz auf CalendarSyncEntry
  notizen_enc?: string;
}

export interface LearnPattern {
  id: string;
  schema_version: 1;
  mandant_id: MandantID;
  signal_features: {
    title_normalized: string;
    location_keyword?: string;
  };
  matched_leistung_id: LeistungsID;
  confidence_boost: number;    // +Punkte für zukünftige Matches
  confirmed_count: number;
  last_used: string;
}

// ── Version 5: Einstellungen + Zeiteinträge ───────────────────────────────

export interface Einstellung {
  key:   string;   // z.B. 'aussteller', 'app_config'
  value: string;   // JSON-String
}

export interface ZeitEintragDB {
  id:           string;
  datum:        string;       // ISO-Date
  startISO:     string;
  endeISO:      string | null;
  dauerMin:     number;
  mandantId:    string;
  leistungCode: string;
  beschreibung: string;
  abgerechnet:  boolean;
  erstelltAm:   string;
}

// ── Version 4: Rechnungen + Mahnwesen ─────────────────────────────────────

export interface Rechnung {
  id: string;
  schema_version: 1;
  rechnungs_nummer: RechnungsNummer;
  mandant_id: MandantID;
  datum: string;               // ISO-Datum
  leistungs_ids: string[];
  betrag_netto: EuroCents;
  ust_betrag: EuroCents;
  betrag_brutto: EuroCents;
  ust_befreit: boolean;        // § 6 Abs. 1 Z 19 UStG
  status: 'ENTWURF' | 'VERSENDET' | 'BEZAHLT' | 'STORNIERT' | 'UEBERFAELLIG';
  faellig_am: string;
  bezahlt_am?: string;
  pdf_blob_enc?: string;       // verschlüsseltes PDF als Base64
  erstellt_am: string;
}

export interface Mahnung {
  id: string;
  schema_version: 1;
  rechnung_id: string;
  stufe: 1 | 2 | 3;
  datum: string;
  betrag_mahngebuehr: EuroCents;
  versendet_am?: string;
}

// ── Version 8: Begehungen (strukturierte Mängelliste) ──────────────────────

export interface Mangel {
  id:          string;
  beschreibung: string;
  prioritaet:  'HOCH' | 'MITTEL' | 'NIEDRIG';
  massnahme:   string;
  frist?:      string;      // ISO-Datum
  foto_b64?:   string;      // Foto des Mangels (clientseitig verkleinert)
  foto_mime?:  string;
  erledigt:    boolean;
}

export interface Begehung {
  id:            string;
  schema_version: 1;
  mandant_id:    MandantID;
  datum:         string;    // ISO-Datum der Begehung
  uhrzeit:       string;    // HH:MM Beginn (leer = keine)
  uhrzeit_ende:  string;    // HH:MM Ende (leer = keine)
  ort:           string;    // Ort/Standort der Begehung
  bereich:       string;    // begehter Bereich/Abteilung
  begleiter:     string;    // Begleitung vor Ort
  allgemein:     string;    // allgemeine/positive Feststellungen
  maengel:       Mangel[];
  status:        'OFFEN' | 'ABGESCHLOSSEN';
  erstellt_am:   string;
  geaendert_am:  string;
}

// ── Version 7: Firmen-Akte ─────────────────────────────────────────────────

/** Chronologischer Akten-Eintrag je Firma: Besprechungsnotiz aus dem
 *  Google-Kalender, zugeordnete E-Mail, manuelles Dokument oder
 *  Foto/Beleg von der Handy-Erfassung. */
export interface AkteEintrag {
  id: string;
  schema_version: 1;
  mandant_id: MandantID;
  typ: 'BESPRECHUNG' | 'EMAIL' | 'DOKUMENT' | 'BELEG';
  titel: string;
  inhalt: string;              // Notiz-Text / E-Mail-Text / Dokument-Inhalt
  datum: string;               // ISO — Termin- bzw. E-Mail-/Beleg-Datum
  quelle_id?: string;          // Google-Event-ID / Gmail-Message-ID (Dedup)
  absender?: string;           // bei EMAIL: From-Adresse
  anhang_b64?: string;         // Foto/PDF (Base64, clientseitig verkleinert)
  anhang_mime?: string;        // z.B. image/jpeg, application/pdf
  // Folge-Aktion („KI schlägt nächste Schritte vor")
  naechster_schritt?: string;  // KI-Vorschlag, z.B. „Zur Steuerberater-Ausgabe"
  erledigt?: boolean;          // Aufgabenliste: Schritt erledigt?
  betrag_cents?: number;       // bei BELEG: erkannter Betrag (für Steuer-CSV)
  erstellt_am: string;
}

// ── Version 9: Erinnerungen ───────────────────────────────────────────────

/** Manuell gesetzte Erinnerung (Knopf an Terminen/Fristen/ToDos). Wird per
 *  Sync auf den Server gebracht; der Telegram-Bot liefert sie am remind_datum
 *  aus (setzt danach gesendet=true). Enthält NUR administrative Daten. */
export interface Erinnerung {
  id:            string;
  schema_version: 1;
  titel:         string;                              // worum es geht (administrativ)
  remind_datum:  string;                              // ISO-Datum, an dem erinnert wird
  typ:           'TERMIN' | 'FRIST' | 'TODO' | 'FREI';
  mandant_id?:   string;                              // optionale Firmenzuordnung (Mandant-ID)
  quelle?:       string;                              // Ursprung, z.B. "begehung:<id>", "akte:<id>", "gcal:<eventId>"
  notiz?:        string;
  erledigt:      boolean;                             // vom Nutzer abgehakt
  gesendet:      boolean;                             // vom Bot bereits per Telegram verschickt
  erstellt_am:   string;
}

// ── Version 10: Besprechungsprotokolle ────────────────────────────────────

/** Eine abhakbare Aufgabe innerhalb eines Besprechungsprotokolls. */
export interface ProtokollAufgabe {
  id:       string;
  text:     string;
  erledigt: boolean;
}

/** Ein Dokument-Anhang am Protokoll (Foto/PDF, Base64 wie Akte-Belege). */
export interface ProtokollAnhang {
  id:   string;
  name: string;   // Dateiname / Anzeigename
  b64:  string;   // Base64-Inhalt (Bild verkleinert / PDF ≤ 4 MB)
  mime: string;   // z.B. image/jpeg, application/pdf
}

/** Besprechungsprotokoll — bearbeitbares, speicherbares Dokument, optional
 *  mit einem Kalender-Termin (event_id) und/oder einer Firma verknüpft. */
export interface Protokoll {
  id:              string;
  schema_version:  1;
  titel:           string;              // Besprechungs-/Termin-Titel
  datum:           string;              // ISO-Datum der Besprechung
  uhrzeit?:        string;              // HH:MM Beginn (optional, z.B. aus dem Termin)
  uhrzeit_ende?:   string;              // HH:MM Ende (optional)
  ort?:            string;              // Ort der Besprechung (optional, z.B. aus dem Termin)
  mandant_id?:     string;              // optionale Firmenzuordnung
  event_id?:       string;              // verknüpfter Google-Kalender-Termin
  teilnehmer:      string;              // Freitext (zeilenweise)
  notizen:         string;              // Freitext (mehrzeilig)
  aufgaben:        ProtokollAufgabe[];
  anhaenge:        ProtokollAnhang[];   // angehängte Dokumente (Fotos/PDFs)
  naechste_sitzung?: string;            // optionales Datum/Text
  erstellt_am:     string;
  geaendert_am:    string;
}

// ── Version 6: Sync-Engine (zentrale DB) ──────────────────────────────────

/** Ausstehende lokale Änderung; key = `${table}:${id}` (eine pro Datensatz). */
export interface SyncOutboxEntry {
  key:         string;   // `${table}:${id}`
  table:       string;
  row_id:      string;
  deleted:     boolean;
  modified_at: string;   // ISO — lokaler Änderungszeitpunkt (LWW am Server)
}

/** Zuletzt vom Server gesehene rev je Datensatz; key = `${table}:${id}`. */
export interface SyncRevEntry {
  key: string;
  rev: number;
}

/** Kleiner Key-Value-Store der Sync-Engine (last_pulled_at, …). */
export interface SyncMetaEntry {
  key:   string;
  value: string;
}

// ── Dexie-Klasse ───────────────────────────────────────────────────────────

export class AmedDB extends Dexie {
  cryptoMeta!:          Table<CryptoMeta, string>;
  mandanten!:           Table<Mandant, MandantID>;
  mitarbeiter!:         Table<Mitarbeiter, MitarbeiterID>;
  arbeitsplaetze!:      Table<Arbeitsplatz, ArbeitsplatzID>;
  vorsorge!:            Table<VorsorgeEintrag, string>;
  audit_log!:           Table<AuditLog, number>;
  // v2
  calendar_sync!:       Table<CalendarSyncEntry, string>;
  // v3
  honorarvereinbarungen!: Table<Honorarvereinbarung, string>;
  leistungserfassung!:  Table<Leistungserfassung, string>;
  learn_patterns!:      Table<LearnPattern, string>;
  // v4
  rechnungen!:          Table<Rechnung, string>;
  mahnungen!:           Table<Mahnung, string>;
  // v5
  einstellungen!:       Table<Einstellung, string>;
  zeiteintraege!:       Table<ZeitEintragDB, string>;
  // v6 — Sync
  sync_outbox!:         Table<SyncOutboxEntry, string>;
  sync_revs!:           Table<SyncRevEntry, string>;
  sync_meta!:           Table<SyncMetaEntry, string>;
  // v7 — Firmen-Akte
  akte!:                Table<AkteEintrag, string>;
  // v8 — Begehungen
  begehungen!:          Table<Begehung, string>;
  // v9 — Erinnerungen
  erinnerungen!:        Table<Erinnerung, string>;
  // v10 — Besprechungsprotokolle
  protokolle!:          Table<Protokoll, string>;

  constructor() {
    super('AmedDB');

    // === VERSION 1 — Initial Schema ===
    this.version(1).stores({
      cryptoMeta:     'id',
      mandanten:      'id, kurz_code, name',
      mitarbeiter:    'id, mandant_id, arbeitsplatz_id',
      arbeitsplaetze: 'id, mandant_id',
      vorsorge:       'id, mitarbeiter_id, mandant_id, faelligkeit_datum, status',
      audit_log:      '++id, timestamp, firma, aktion',
    });

    // === VERSION 2 — Calendar-Sync ===
    this.version(2).stores({
      calendar_sync: 'id, iCalUID, status, synced_at, matched_mandant_id',
    });

    // === VERSION 3 — Honorar + Leistungserfassung ===
    this.version(3).stores({
      honorarvereinbarungen: 'id, mandant_id, gueltig_ab',
      leistungserfassung:    'id, datum, mandant_id, leistung_id, abgerechnet, rechnungs_id',
      learn_patterns:        'id, mandant_id, signal_features.title_normalized',
    }).upgrade(async (trans) => {
      // Vorhandene Vorsorge-Einträge ohne status bekommen 'OFFEN'
      await trans.table('vorsorge').toCollection().modify((eintrag: VorsorgeEintrag) => {
        if (!eintrag.status) eintrag.status = 'OFFEN';
      });
    });

    // === VERSION 4 — Rechnungs-Archiv + Mahnwesen ===
    this.version(4).stores({
      rechnungen: 'id, rechnungs_nummer, mandant_id, datum, status, betrag_brutto, faellig_am',
      mahnungen:  'id, rechnung_id, stufe, datum',
    });

    // === VERSION 5 — Einstellungen + Zeiteinträge ===
    this.version(5).stores({
      einstellungen: 'key',
      zeiteintraege: 'id, datum, mandantId, leistungCode, abgerechnet',
    });

    // === VERSION 6 — Sync-Engine (zentrale DB, Offline-Sync) ===
    this.version(6).stores({
      sync_outbox: 'key, table',
      sync_revs:   'key',
      sync_meta:   'key',
    });

    // === VERSION 7 — Firmen-Akte ===
    this.version(7).stores({
      akte: 'id, mandant_id, typ, datum, quelle_id',
    });

    // === VERSION 8 — Begehungen (strukturierte Mängelliste) ===
    this.version(8).stores({
      begehungen: 'id, mandant_id, datum, status',
    });

    // === VERSION 9 — Erinnerungen (Knopf an Terminen/Fristen/ToDos) ===
    this.version(9).stores({
      erinnerungen: 'id, remind_datum, typ, erledigt, gesendet, mandant_id',
    });

    // === VERSION 10 — Besprechungsprotokolle ===
    this.version(10).stores({
      protokolle: 'id, datum, mandant_id, event_id',
    });
  }
}

export const db = new AmedDB();
